// priorityClips — decide which body clips must be loaded BEFORE the avatar is
// marked ready, so character load doesn't block on every animation GLB.
//
// At boot only a handful of clips are actually on screen: the clips in each
// state machine's INITIAL state (the base idle layer, breathing, fidget, and
// the conversational SM's entry state). Everything else — gestures, prefab
// one-shots, listening/thinking/talking pools — is reachable only after an
// event fires, so it can stream in the background (BodyClipLoader) and be pulled
// on demand (GraphEvaluator's missing-action hook).
//
// This walk mirrors the runtime's own entry resolution in
// updateSMTransitions(): a SM's initial state is the target of the edge whose
// source is the 'entry' node. State inner graphs are themselves mini blueprint
// graphs, so we recurse through nested playClip nodes and nested state machines.

const IDLE_RE = /idle/i

/**
 * @param {Array} bodyGraphNodes  graphState.nodes for the body blueprint.
 * @param {Array<string>} allBodyClipNames  every body clip the character has.
 * @param {{cap?: number}} [opts]
 * @returns {string[]} clip names to load before ready (deduped, capped, filtered to known clips).
 */
export function computePriorityClipNames(bodyGraphNodes, allBodyClipNames, { cap = 8 } = {}) {
  const known = new Set(allBodyClipNames ?? [])
  const out = []
  const seen = new Set()
  const add = (name) => {
    if (name && !seen.has(name)) { seen.add(name); out.push(name) }
  }

  const fromNodes = (nodes) => { for (const n of nodes ?? []) collectNode(n) }

  function collectNode(n) {
    if (!n) return
    const data = n.data ?? {}
    if (n.type === 'playClip') {
      add(data.clipName)
      return
    }
    if (n.type === 'stateMachine') {
      // Only the INITIAL state is active on the first frames.
      const innerEdges = data.innerEdges ?? []
      const innerNodes = data.innerNodes ?? []
      const entryEdge = innerEdges.find(e => e.source === 'entry')
      const initial = innerNodes.find(s => s.id === entryEdge?.target)
        ?? innerNodes.find(s => s.type === 'state') // fallback: first state
      collectNode(initial)
      return
    }
    if (n.type === 'state') {
      // A state may use a clips[] random pool and/or nested playClip / SM nodes.
      if (Array.isArray(data.clips)) data.clips.forEach(add)
      fromNodes(data.innerNodes)
      return
    }
    // Any other container node (blend, etc.) — descend if it has inner nodes.
    if (data.innerNodes) fromNodes(data.innerNodes)
  }

  fromNodes(bodyGraphNodes)

  // Heuristic fallback: ensure idle clips are present. Covers an empty or
  // late-loading graph (the blueprint hydrates from localStorage independently
  // of loadAll) and guarantees a non-T-pose at ready.
  for (const name of known) {
    if (IDLE_RE.test(name)) add(name)
  }

  // Keep only clips the character actually has (when a clip list was provided).
  let result = known.size === 0 ? out.slice() : out.filter(n => known.has(n))

  // If everything filtered out, fall back to known idle clips.
  if (result.length === 0 && known.size > 0) {
    result = [...known].filter(n => IDLE_RE.test(n))
  }

  // Dedup, then cap.
  const deduped = []
  const dedupSeen = new Set()
  for (const n of result) {
    if (!dedupSeen.has(n)) { dedupSeen.add(n); deduped.push(n) }
  }
  let capped = deduped.slice(0, cap)

  // Guarantee at least one idle clip survives WITHIN the cap, if one exists —
  // an idle present but beyond the cap would still leave a T-pose at ready.
  if (!capped.some(n => IDLE_RE.test(n))) {
    const idle = deduped.find(n => IDLE_RE.test(n)) ?? [...known].find(n => IDLE_RE.test(n))
    if (idle) capped = [idle, ...capped.filter(n => n !== idle)].slice(0, cap)
  }
  return capped
}
